import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import trackShowMore from '@helpers/analyticsHelpers/trackShowMoreProductInfo';
import { StyledExandableContainer, StyledExandableContainerButton } from './ExpandableContainer.styles';

interface Props {
  children: ReactNode;
  meta: string;
  initiallyExpanded?: boolean;
}

const ExpandableContainer = ({ children, meta, initiallyExpanded }: Props) => {
  const setInitiallyExpanded = initiallyExpanded || false;
  const [isExpanded, setIsExpanded] = useState(setInitiallyExpanded);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const t = useTranslations('product');
  const contentRef = useRef<HTMLDivElement>(null);

  const expandableMaxHeight = 150;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setIsOverflowed(el.getBoundingClientRect().height > expandableMaxHeight);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, []);

  const onButtonClick = (e: React.MouseEvent) => {
    // Prevent the blur click from being triggered
    e.stopPropagation();
    if (!isExpanded) {
      trackShowMore(meta);
    }
    setIsExpanded(!isExpanded);
  };

  const onBlurClick = () => {
    if (!isExpanded) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div onClick={onBlurClick}>
      <StyledExandableContainer
        $faded={!isExpanded && isOverflowed}
        height={!isExpanded ? expandableMaxHeight : 'auto'}
        data-testid="expandable-container-animated-area"
      >
        <div ref={contentRef} data-testid="expandable-container-content">
          {children}
        </div>
      </StyledExandableContainer>
      {isOverflowed && (
        <StyledExandableContainerButton
          variant="link"
          onClick={onButtonClick}
          data-testid="expandable-container-button"
        >
          {t(isExpanded ? 'expandableContainer.showLess' : 'expandableContainer.showMore')}
        </StyledExandableContainerButton>
      )}
    </div>
  );
};

export default ExpandableContainer;
